/**
 * components/statChip.tsx
 * Compact premium KPI chip for list headers — a big mono number beside a small
 * uppercase label, with an optional tone dot. The primary chip (`emphasis`)
 * gets a filled accent surface so the headline total reads first. Grayscale-only
 * (tone dots are greys), hover-lifts on web. Used by the Jobs & Candidates
 * headers to surface totals clearly instead of a tiny badge.
 */
import React from 'react';
import { View, Text, Pressable } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useHover } from '../hooks/useHover';
import { web } from '../utils/web';
import { toneColors, type Tone } from '../theme/tone';

export interface StatChipProps {
  value: number | string;
  label: string;
  /** Grey tone dot before the number (meaning by lightness, never hue). */
  tone?: Tone;
  /** The headline chip (e.g. "Total") — filled accent surface. */
  emphasis?: boolean;
  /** Makes the chip a filter switch — pressing it selects the matching tab. */
  onPress?: () => void;
  /** Pressable chips only: renders the selected (currently-filtered) state. */
  selected?: boolean;
}

export default function StatChip({ value, label, tone, emphasis, onPress, selected }: StatChipProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font, radii } = theme;
  const { hovered, hoverProps } = useHover();
  const dot = tone ? toneColors(colors, tone).fg : null;
  const Container: any = onPress ? Pressable : View;
  // Selected switch = filled ink surface with inverted contents, matching the
  // ticked status tabs below so the two controls read as one selection.
  const numberInk = selected ? colors.onAccent : colors.ink;
  const labelInk = selected ? colors.onAccent : colors.ink3;
  const dotInk = selected ? colors.onAccent : dot;

  return (
    <Container
      {...hoverProps}
      {...(onPress
        ? { onPress, accessibilityRole: 'tab', accessibilityState: { selected: !!selected }, accessibilityLabel: `${label} — ${value}` }
        : null)}
      style={[
        {
          flexDirection: 'row',
          alignItems: 'center',
          gap: 9,
          paddingVertical: 9,
          paddingHorizontal: 14,
          borderRadius: radii.lg,
          borderWidth: 1,
          borderColor: selected ? colors.accentInk : emphasis ? colors.accentLine : colors.line,
          backgroundColor: selected ? colors.accentInk : emphasis ? colors.accentSoft : colors.card,
        },
        onPress && web({ cursor: 'pointer' }),
        web({ transition: 'transform 150ms cubic-bezier(.2,.7,.2,1), box-shadow 150ms ease, border-color 150ms ease, background-color 150ms ease' }),
        hovered && web({ transform: 'translateY(-1px)', boxShadow: '0 8px 20px -12px rgba(0,0,0,0.28)', borderColor: selected ? colors.accentInk : colors.lineStrong }),
      ]}
    >
      {dotInk && <View style={{ width: 8, height: 8, borderRadius: radii.pill, backgroundColor: dotInk }} />}
      <Text style={{ fontFamily: font.mono, fontSize: fontSize['2xl'], fontWeight: fontWeight.bold, color: numberInk, letterSpacing: -0.5, lineHeight: fontSize['2xl'] * 1.05 }}>
        {value}
      </Text>
      <Text style={{ fontSize: 10, letterSpacing: 0.7, textTransform: 'uppercase', color: labelInk, fontWeight: fontWeight.semibold }}>
        {label}
      </Text>
    </Container>
  );
}
